import * as React from "react";
import { useCallback, useState } from "react";
import styled, { css, createGlobalStyle } from "styled-components";
import { flexRender } from "@tanstack/react-table";
import {
  columnTransformVar,
  columnOpacityVar,
  columnWidthVar,
  columnTransition,
  displayScrollRightIndicatorVar
} from "components/common/EntityDataTable/CSSVariables";
import { ACTIONS_COL_ID, RESIZE_HIT_AREA_OVERHANG } from "components/common/EntityDataTable/Constants";
import ScrollShadow from "theme/box-shadows/ScrollShadow";
import useForceUpdate from "util/hooks/useForceUpdate";
import zIndices from "theme/z-indices";
const Thead = styled.thead(
  ({ theme }) => css`
    background-color: ${theme.colors.global.contentBackground};

    tr {
      background-color: ${theme.colors.table.head.background};
    }
  `
);
export const Th = styled.th(
  ({
    $colId,
    $hideEmptyTailBorder,
    $hideEmptyTailPinShadow,
    $hidePadding,
    $pinningPosition,
    $revealHeaderActions,
    $zIndex,
    theme
  }) => css`
    position: relative;
    padding: 0 !important;
    z-index: ${$pinningPosition ? zIndices.tableHeaderCellPinned : $zIndex};
    font-weight: normal; // override the browser default bold th styling
    border-right-color: ${theme.colors.table.row.divider} !important;
    width: var(${columnWidthVar($colId)});
    opacity: var(${columnOpacityVar($colId)}, 1);
    transform: var(${columnTransformVar($colId)}, translate3d(0, 0, 0));
    transition: var(${columnTransition()}, none);
    height: 100%; // required to be able to use height: 100% in child elements
    && {
      background-color: transparent;
    }

    ${$revealHeaderActions && css`
      .header-action {
        opacity: 1;
      }
    `}

    ${$pinningPosition ? css`
          position: sticky;
          ${$pinningPosition === "left" ? "left" : "right"}: 0;
          ${$pinningPosition === "right" && !$hideEmptyTailPinShadow && css`
            box-shadow: inset -1px 0 0 ${theme.colors.table.row.divider};
          `}
        ` : ""}

    ${$hidePadding && css`
      && {
        padding: 0;
      }
    `}

    ${$colId === ACTIONS_COL_ID && css`
      position: sticky;
      ${ScrollShadow("left")}
      &::before {
        display: var(${displayScrollRightIndicatorVar}, none);
      }
    `}

    ${$hideEmptyTailBorder && css`
      && {
        border-right: none;
      }
    `}
  `
);
const ResizeHitArea = styled.div(
  ({ theme, $isResizing, $constrainOverhang }) => css`
    position: absolute;
    top: 0;
    bottom: 0;
    right: ${$constrainOverhang ? 0 : -RESIZE_HIT_AREA_OVERHANG}px;
    width: ${RESIZE_HIT_AREA_OVERHANG * ($constrainOverhang ? 1 : 2)}px;
    cursor: col-resize;
    touch-action: none;
    user-select: none;
    z-index: 1;

    &::after {
      content: '';
      position: absolute;
      top: 0;
      bottom: 0;
      width: 3px;
      transform: translateX(-50%);
      background-color: ${theme.colors.variant.info};
      opacity: ${$isResizing ? 1 : 0};
      transition: opacity 0.15s ease-in-out;
      ${$constrainOverhang ? css`
            right: -2px;
          ` : css`
            left: 50%;
          `};
    }

    &:hover::after {
      opacity: 1;
    }
  `
);
const RESIZING_BODY_CLASS = "entity-data-table-resizing-column";
const ResizeCursorGlobalStyle = createGlobalStyle`
  body.${RESIZING_BODY_CLASS}, body.${RESIZING_BODY_CLASS} * {
    cursor: col-resize !important;
  }
`;
const TableHeaderCell = ({
  header,
  isTailColumnEmpty,
  constrainResizeOverhang
}) => {
  var _a, _b;
  const columnMeta = header.column.columnDef.meta;
  const isActionsColumn = header.column.id === ACTIONS_COL_ID;
  const forceUpdate = useForceUpdate();
  const [isHoveringResizeHandle, setIsHoveringResizeHandle] = useState(false);
  const bindResizeHandler = useCallback(
    (handler) => (event) => {
      handler(event);
      forceUpdate();
      document.body.classList.add(RESIZING_BODY_CLASS);
      const stopResizing = () => {
        forceUpdate();
        document.body.classList.remove(RESIZING_BODY_CLASS);
        window.removeEventListener("mouseup", stopResizing);
        window.removeEventListener("touchend", stopResizing);
      };
      window.addEventListener("mouseup", stopResizing);
      window.addEventListener("touchend", stopResizing);
    },
    [forceUpdate]
  );
  return /* @__PURE__ */ React.createElement(
    Th,
    {
      key: header.id,
      colSpan: header.colSpan,
      $colId: header.column.id,
      $hideEmptyTailBorder: isActionsColumn && isTailColumnEmpty,
      $hideEmptyTailPinShadow: isActionsColumn && !header.column.columnDef.header,
      $hidePadding: columnMeta == null ? void 0 : columnMeta.hideCellPadding,
      $pinningPosition: header.column.getIsPinned(),
      $zIndex: zIndices.tableHeaderCell - header.index,
      $revealHeaderActions: isHoveringResizeHandle || header.column.getIsResizing()
    },
    header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext()),
    header.column.getCanResize() && /* @__PURE__ */ React.createElement(
      ResizeHitArea,
      {
        onMouseDown: bindResizeHandler(header.getResizeHandler()),
        onTouchStart: bindResizeHandler(header.getResizeHandler()),
        onMouseEnter: () => setIsHoveringResizeHandle(true),
        onMouseLeave: () => setIsHoveringResizeHandle(false),
        $isResizing: header.column.getIsResizing(),
        $constrainOverhang: constrainResizeOverhang,
        role: "separator",
        "aria-label": `Resize ${(_a = columnMeta == null ? void 0 : columnMeta.label) != null ? _a : header.column.id} column`,
        title: `Resize ${(_b = columnMeta == null ? void 0 : columnMeta.label) != null ? _b : header.column.id} column`
      }
    )
  );
};
const TableHead = ({ columnWidths, headerGroups }) => {
  const isTailColumnEmpty = !columnWidths[ACTIONS_COL_ID];
  return /* @__PURE__ */ React.createElement(Thead, null, /* @__PURE__ */ React.createElement(ResizeCursorGlobalStyle, null), headerGroups.map((headerGroup) => /* @__PURE__ */ React.createElement("tr", { key: headerGroup.id }, headerGroup.headers.map((header, index) => /* @__PURE__ */ React.createElement(
    TableHeaderCell,
    {
      key: header.id,
      header,
      isTailColumnEmpty,
      constrainResizeOverhang: isTailColumnEmpty && index === headerGroup.headers.length - 2
    }
  )))));
};
export default TableHead;
